import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Anchor, Timeline } from '@any-ui-react/core';

import {
  CustomerActivityTimelineItem,
  CustomerActivityTimelineItemProps,
  CustomerActivityTimelineNoteItem,
} from '../customer-activity-timeline-item';

type CustomerActivityBaseProps = Pick<CustomerActivityTimelineItemProps, 'channel' | 'time'>;

export type CustomerCreatedActivityProps = CustomerActivityBaseProps;

export const CustomerCreatedActivity = ({ channel, time }: CustomerCreatedActivityProps) => {
  const { t } = useTranslation(['anychat', 'gql']);

  return (
    <CustomerActivityTimelineItem
      channel={channel}
      content={t('anychat.shared.customerActivityTimeline.activity.customerCreated', {
        channel: t('gql.enum.crmChannel', { channel, ns: 'gql' }),
      })}
      time={time}
    />
  );
};

export interface CustomerOrderedActivityProps extends CustomerActivityBaseProps {
  orderId: string;
  orderUrl?: string;
}

export const CustomerOrderedActivity = ({
  channel,
  orderId,
  orderUrl,
  time,
}: CustomerOrderedActivityProps) => {
  const { t } = useTranslation(['anychat', 'gql']);

  return (
    <CustomerActivityTimelineItem
      channel={channel}
      content={
        <Trans
          ns="anychat"
          i18nKey="anychat.shared.customerActivityTimeline.activity.customerOrdered"
          values={{
            orderId,
            channel: t('gql.enum.crmChannel', { channel, ns: 'gql' }),
          }}
          components={[
            orderUrl ? (
              <Anchor component={Link} to={orderUrl} target="_blank">
                {orderId}
              </Anchor>
            ) : (
              <span>{orderId}</span>
            ),
          ]}
        />
      }
      time={time}
    />
  );
};

export interface CustomerProfileUpdatedActivityProps extends CustomerActivityBaseProps {
  userName: string;
}

export const CustomerProfileUpdatedActivity = ({
  userName,
  channel,
  time,
}: CustomerProfileUpdatedActivityProps) => {
  const { t } = useTranslation('anychat');

  return (
    <CustomerActivityTimelineItem
      channel={channel}
      content={t('anychat.shared.customerActivityTimeline.activity.profileUpdated', {
        userName,
      })}
      time={time}
    />
  );
};

export type CustomerOptInRequestedActivityProps = CustomerActivityBaseProps;

export const CustomerOptInRequestedActivity = ({
  channel,
  time,
}: CustomerOptInRequestedActivityProps) => {
  const { t } = useTranslation(['anychat', 'gql']);

  return (
    <CustomerActivityTimelineItem
      channel={channel}
      content={t('anychat.shared.customerActivityTimeline.activity.optInRequested', {
        channel: t('gql.enum.crmChannel', { channel, ns: 'gql' }),
      })}
      time={time}
    />
  );
};

export type CustomerOptInAcceptedActivityProps = CustomerActivityBaseProps;

export const CustomerOptInAcceptedActivity = ({
  channel,
  time,
}: CustomerOptInAcceptedActivityProps) => {
  const { t } = useTranslation(['anychat', 'gql']);

  return (
    <CustomerActivityTimelineItem
      channel={channel}
      content={t('anychat.shared.customerActivityTimeline.activity.optInAccepted', {
        channel: t('gql.enum.crmChannel', { channel, ns: 'gql' }),
      })}
      time={time}
    />
  );
};

export type CustomerOptedOutActivityProps = CustomerActivityBaseProps;

export const CustomerOptedOutActivity = ({ channel, time }: CustomerOptedOutActivityProps) => {
  const { t } = useTranslation(['anychat', 'gql']);

  return (
    <CustomerActivityTimelineItem
      channel={channel}
      content={t('anychat.shared.customerActivityTimeline.activity.optedOut', {
        channel: t('gql.enum.crmChannel', { channel, ns: 'gql' }),
      })}
      time={time}
    />
  );
};

export interface CustomerNotedActivityProps extends CustomerActivityBaseProps {
  id: string;
  note: string;
  editor: string;
}

export const CustomerNotedActivity = ({ ...props }: CustomerNotedActivityProps) => {
  return (
    <Timeline.Item
      classNames={{
        itemBody: 'mb-4',
        itemBullet: 'hidden',
        item: 'mt-0 pl-0! before:left-4!',
      }}
    >
      <CustomerActivityTimelineNoteItem editable {...props} />
    </Timeline.Item>
  );
};

export interface InfluencerJoinedCampaignActivityProps
  extends Omit<CustomerActivityBaseProps, 'channel'> {
  campaignName: string;
  campaignUrl: string;
}

export const InfluencerJoinedCampaignActivity = ({
  time,
  campaignName,
  campaignUrl,
}: InfluencerJoinedCampaignActivityProps) => {
  const { t } = useTranslation('anychat');

  return (
    <CustomerActivityTimelineItem
      channel="ANYCREATOR"
      content={
        <Trans
          t={t}
          ns="anychat"
          i18nKey="anychat.shared.customerActivityTimeline.activity.influencer.joinedCampaign"
          components={[
            <Anchor className="font-semibold" href={campaignUrl} target="_blank" rel="noreferrer">
              {campaignName}
            </Anchor>,
          ]}
          values={{
            campaignUrl,
            campaignName,
          }}
        />
      }
      time={time}
    />
  );
};

export interface InfluencerSubmittedPostActivityProps
  extends Omit<CustomerActivityBaseProps, 'channel'> {
  campaignName: string;
  campaignUrl: string;
}

export const InfluencerSubmittedPostActivity = ({
  time,
  campaignName,
  campaignUrl,
}: InfluencerSubmittedPostActivityProps) => {
  const { t } = useTranslation('anychat');

  return (
    <CustomerActivityTimelineItem
      channel="ANYCREATOR"
      content={
        <Trans
          t={t}
          ns="anychat"
          i18nKey="anychat.shared.customerActivityTimeline.activity.influencer.submittedPost"
          components={[
            <Anchor className="font-semibold" href={campaignUrl} target="_blank" rel="noreferrer">
              {campaignName}
            </Anchor>,
          ]}
          values={{
            campaignUrl,
            campaignName,
          }}
        />
      }
      time={time}
    />
  );
};

export interface InfluencerPublishedPostActivityProps
  extends Omit<CustomerActivityBaseProps, 'channel'> {
  campaignName: string;
  campaignUrl: string;
}

export const InfluencerPublishedPostActivity = ({
  time,
  campaignName,
  campaignUrl,
}: InfluencerPublishedPostActivityProps) => {
  const { t } = useTranslation('anychat');

  return (
    <CustomerActivityTimelineItem
      channel="ANYCREATOR"
      content={
        <Trans
          t={t}
          ns="anychat"
          i18nKey="anychat.shared.customerActivityTimeline.activity.influencer.publishedPost"
          components={[
            <Anchor className="font-semibold" href={campaignUrl} target="_blank" rel="noreferrer">
              {campaignName}
            </Anchor>,
          ]}
          values={{
            campaignUrl,
            campaignName,
          }}
        />
      }
      time={time}
    />
  );
};
